<script setup lang="ts">
/** Organizations offered to you: accept (if it fits your plan) or decline. */
import { onMounted, ref } from 'vue';
import type { OwnershipOffer } from '../../app/api/OrgsApi.ts';
import { useApi } from '../../app/api/index.ts';
import { useNotice } from '../../app/composables/useNotice.ts';
import { useSessionStore } from '../../app/stores/session.ts';
import { useWorkspaceStore } from '../../app/stores/workspace.ts';
import DAlert from '../../design/components/DAlert.vue';
import DButton from '../../design/components/DButton.vue';

const api = useApi();
const notice = useNotice();
const session = useSessionStore();
const workspace = useWorkspaceStore();
const offers = ref<OwnershipOffer[]>([]);
const busy = ref<string | null>(null);

async function load() {
  try {
    offers.value = (await api.orgs.myOwnershipOffers()).offers;
  } catch (err) {
    notice.fail(err, 'Couldn’t load organizations offered to you.', null);
  }
}
onMounted(load);

async function answer(offer: OwnershipOffer, accept: boolean) {
  busy.value = offer.org.id;
  try {
    if (accept) {
      await api.orgs.acceptOwnership(offer.org.id);
      await Promise.all([workspace.loadOrgs(), session.refresh()]);
      notice.show(`You own ${offer.org.name} now.`, 'success');
    } else {
      await api.orgs.declineOwnership(offer.org.id);
    }
  } catch (err) {
    notice.fail(err, 'That didn’t work. Try again.', null);
  } finally {
    busy.value = null;
    await load();
  }
}
</script>

<template>
  <DAlert v-for="offer in offers" :key="offer.org.id">
    <div class="account-banner">
      <span class="account-banner__text">
        {{ offer.from.name }} offered you ownership of {{ offer.org.name }}. It would count toward
        your plan, and {{ offer.from.name }} would stay on as an admin.
      </span>
      <DButton
        variant="primary"
        size="sm"
        :loading="busy === offer.org.id"
        @click="answer(offer, true)"
        >Accept</DButton
      >
      <DButton variant="ghost" size="sm" :disabled="busy !== null" @click="answer(offer, false)"
        >Decline</DButton
      >
    </div>
  </DAlert>
</template>
